import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentTemplateSelector from './DocumentTemplateSelector';
import { apiFetch } from './utils/api';
import { generateDocumentOutput } from './utils/documentGeneration';
import { getCompanyVariables } from './utils/templateVariables';
import ReceiveGoodsModal from './ReceiveGoodsModal';

function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showDocumentTemplateSelector, setShowDocumentTemplateSelector] = useState(false);

  useEffect(() => {
    fetchPO();
  }, [id]);

  const fetchPO = async () => {
    try {
      const response = await apiFetch(`/api/purchase-orders/${id}`);
      const data = await response.json();
      setPo(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching PO:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      sent: 'bg-blue-500',
      received: 'bg-green-600',
      cancelled: 'bg-red-600',
      overdue: 'bg-orange-600',
      delayed: 'bg-amber-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: '📝',
      sent: '📤',
      received: '✅',
      cancelled: '❌',
      overdue: '⚠️',
      delayed: '⏳'
    };
    return icons[status] || '📦';
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiFetch(`/api/purchase-orders/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      fetchPO();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const preparePurchaseOrderData = async (purchaseOrder) => {
    let companySettings = {};
    try {
      const res = await apiFetch('/api/company-settings');
      companySettings = await res.json();
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }

    const items = purchaseOrder.items || [];

    return {
      supplier_name: purchaseOrder.supplier_name || '',
      supplier_contact: purchaseOrder.supplier_contact || '',
      supplier_email: purchaseOrder.supplier_email || '',
      supplier_phone: purchaseOrder.supplier_phone || '',
      supplier_address: purchaseOrder.supplier_address || '',
      po_number: purchaseOrder.po_number || purchaseOrder.id || '',
      purchase_order_number: purchaseOrder.po_number || purchaseOrder.id || '',
      order_date: purchaseOrder.order_date ? new Date(purchaseOrder.order_date).toLocaleDateString() : '',
      expected_date: purchaseOrder.expected_date ? new Date(purchaseOrder.expected_date).toLocaleDateString() : '',
      expected_delivery: purchaseOrder.expected_date ? new Date(purchaseOrder.expected_date).toLocaleDateString() : '',
      status: purchaseOrder.status || '',
      warehouse_name: purchaseOrder.warehouse_name || '',
      job_number: purchaseOrder.job_number || '',
      sales_order_number: purchaseOrder.sales_order_number || '',
      notes: purchaseOrder.notes || '',
      items: items.map((item) => {
        const lineTotal = formatCurrency(item.line_total || ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)));
        return {
        product_name: item.product_name || item.description || '',
        product_code: item.product_code || '',
        description: item.description || item.product_name || '',
        quantity: parseFloat(item.quantity) || 0,
        unit_price: formatCurrency(item.unit_price || 0),
        line_total: lineTotal,
        total: lineTotal,
      };
      }),
      total_amount: formatCurrency(purchaseOrder.total_amount || 0),
      total: formatCurrency(purchaseOrder.total_amount || 0),
      ...getCompanyVariables(companySettings)
    };
  };

  const handleDocumentTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      const templateData = await preparePurchaseOrderData(po);
      await generateDocumentOutput(template, action, templateData, `PurchaseOrder-${po.po_number || po.id}`, outputWindow);
      setShowDocumentTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Purchase order not found</p>
          <button
            onClick={() => navigate('/purchase-orders')}
            className="text-blue-600 hover:text-blue-700"
          >
            ← Back to Purchase Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/purchase-orders')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">{po.po_number}</h1>
              <p className="text-sm text-blue-100">{po.supplier_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDocumentTemplateSelector(true)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
              >
                Print / PDF
              </button>

              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(po.status)} text-white`}>
                {getStatusIcon(po.status)} {po.status?.toUpperCase()}
              </span>

              {po.status === 'draft' && (
                <button
                  onClick={() => handleStatusChange('sent')}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                >
                  Send to Supplier
                </button>
              )}

              {po.status === 'sent' && (
                <button
                  onClick={() => setShowReceiveModal(true)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                >
                  Receive Goods
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-[72px] z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-4 border-b-2 font-medium ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-3 px-4 border-b-2 font-medium ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              History
            </button>
            {(po.job_number || po.sales_order_number) && (
              <button
                onClick={() => setActiveTab('related')}
                className={`py-3 px-4 border-b-2 font-medium ${
                  activeTab === 'related'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Related
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500 mb-1">Order Date</p>
                <p className="text-lg font-semibold">{formatDate(po.order_date)}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500 mb-1">Expected Delivery</p>
                <p className="text-lg font-semibold">{formatDate(po.expected_date)}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500 mb-1">Warehouse</p>
                <p className="text-lg font-semibold">{po.warehouse_name || 'Not specified'}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500 mb-1">Total Amount</p>
                <p className="text-lg font-bold text-blue-700">{formatCurrency(po.total_amount)}</p>
              </div>
            </div>

            {/* Linked To */}
            {(po.job_number || po.sales_order_number) && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      Linked to:{' '}
                      <button
                        onClick={() => {
                          if (po.job_number) navigate(`/jobs/${po.linked_id}`);
                          else if (po.sales_order_number) navigate(`/sales-orders/${po.linked_id}`);
                        }}
                        className="font-semibold hover:underline"
                      >
                        {po.job_number ? `Job #${po.job_number}` : `Sales Order #${po.sales_order_number}`}
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Items Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Order Items</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {po.items?.map((item, index) => (
                      <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.product_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.product_code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {parseFloat(item.quantity).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                          {formatCurrency(item.line_total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                        Total:
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-bold text-blue-700">
                        {formatCurrency(po.total_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Notes */}
            {po.notes && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{po.notes}</p>
              </div>
            )}

            {/* Supplier Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Supplier Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Contact</p>
                  <p className="text-gray-900">{po.supplier_contact || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-gray-900">{po.supplier_phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-900">{po.supplier_email || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order History</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Purchase Order Created</p>
                  <p className="text-sm text-gray-500">{formatDate(po.created_at)} by {po.created_by_name}</p>
                </div>
              </div>

              {po.status !== 'draft' && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Sent to Supplier</p>
                    <p className="text-sm text-gray-500">{po.supplier_name}</p>
                  </div>
                </div>
              )}

              {po.received_date && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Goods Received</p>
                    <p className="text-sm text-gray-500">{formatDate(po.received_date)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'related' && (po.job_number || po.sales_order_number) && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Related {po.job_number ? 'Job' : 'Sales Order'}</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">This purchase order was created for:</p>
              <button
                onClick={() => {
                  if (po.job_number) navigate(`/jobs/${po.linked_id}`);
                  else if (po.sales_order_number) navigate(`/sales-orders/${po.linked_id}`);
                }}
                className="text-lg font-semibold text-blue-700 hover:text-blue-800 hover:underline"
              >
                {po.job_number ? `Job #${po.job_number}` : `Sales Order #${po.sales_order_number}`}
              </button>
              <p className="text-sm text-gray-500 mt-2">
                Materials will be automatically allocated to this {po.job_number ? 'job' : 'order'} upon receipt.
              </p>
            </div>
          </div>
        )}
      </div>

      <DocumentTemplateSelector
        isOpen={showDocumentTemplateSelector}
        onClose={() => setShowDocumentTemplateSelector(false)}
        documentType="purchase_order"
        placementKey="purchase_order.print_pdf"
        data={po}
        onGenerate={handleDocumentTemplateGenerate}
      />

      {/* Receive Goods Modal */}
      {showReceiveModal && (
        <ReceiveGoodsModal
          po={po}
          onClose={() => setShowReceiveModal(false)}
          onSuccess={() => {
            setShowReceiveModal(false);
            fetchPO(); // Refresh PO data
          }}
        />
      )}
    </div>
  );
}

export default PurchaseOrderDetail;
